import React from "react";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip,
  CartesianGrid, ReferenceLine,
} from "recharts";
import {
  ArrowUpRight, FileText, Tag, UserPlus, Wallet, Plus, ChevronRight, Activity,
  CheckCircle2, AlertTriangle, Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { format, humanizeStatus } from "@/lib/brand-tokens";
import {
  getAdminDashboardStats, getCardsProcessedSeries, getPaymentVolumeSeries,
  adminUsers, getAdminById,
} from "@/lib/mock-data";
import { Gated } from "@/components/Gated";

// ADM-01 — Admin dashboard
export default function AdminDashboardPage() {
  const stats = getAdminDashboardStats();
  const cardSeries = getCardsProcessedSeries();
  const volSeries = getPaymentVolumeSeries();
  const cardAvg = Math.round(cardSeries.reduce((a, x) => a + x.cards, 0) / cardSeries.length);
  const cardTotal = cardSeries.reduce((a, x) => a + x.cards, 0);
  const volTotal = volSeries.reduce((a, x) => a + x.volume, 0);

  return (
    <div data-testid="admin-dashboard-page" className="space-y-6 pb-10">
      {/* Header */}
      <div className="flex items-end justify-between gap-6">
        <div>
          <div className="eyebrow">Operations</div>
          <h1 className="font-brand text-display text-ink leading-tight mt-1">Dashboard</h1>
          <p className="text-body-sm text-ink-muted mt-1">Live operational state · {format.dateTime(new Date())}</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/deals/new">
            <Gated action="deals.create">
              <Button className="bg-brand-700 hover:bg-brand-800 text-white" data-testid="dashboard-new-deal-btn"><Plus className="size-4 mr-1.5" /> New deal</Button>
            </Gated>
          </Link>
          <Link to="/admin/payments">
            <Gated action="payments.generateRun">
              <Button variant="outline" data-testid="dashboard-generate-run-btn">Generate payment run</Button>
            </Gated>
          </Link>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={<Tag className="size-4" />} label="Active deals" value={stats.activeDealsCount}
          subline={`${format.currency(stats.totalCapDeployed)} deployed · ${Math.round((stats.totalCapUsed / stats.totalCapDeployed) * 100)}% used`}
          link="/admin/deals" tone="brand" />
        <StatCard
          icon={<UserPlus className="size-4" />} label="Pending applications" value={stats.pendingApplicationsCount}
          subline="FIFO queue · review now" link="/admin/applications" tone="info" attention={stats.pendingApplicationsCount > 0} />
        <StatCard
          icon={<FileText className="size-4" />} label="Flagged submissions" value={stats.flaggedSubmissionsCount}
          subline="Awaiting admin review" link="/admin/submissions" tone="flagged" attention={stats.flaggedSubmissionsCount > 0} />
        <StatCard
          icon={<Wallet className="size-4" />} label="Payment runs ready" value={stats.paymentRunsReady}
          subline="Generated, pending dispatch" link="/admin/payments" tone="warning" attention={stats.paymentRunsReady > 0} />
      </div>

      {/* Charts row */}
      <div className="grid lg:grid-cols-3 gap-4">
        {/* Cards processed line chart (2 cols) */}
        <div className="lg:col-span-2 bg-surface border border-line rounded-xl p-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="eyebrow">Cards processed</div>
              <div className="mt-1 flex items-baseline gap-3">
                <span className="font-brand text-h1 text-ink tabular-nums">{format.count(cardTotal)}</span>
                <span className="text-caption text-ink-subtle">last 30 days · avg {cardAvg}/day</span>
              </div>
            </div>
            <Link to="/admin/audit" className="text-caption text-brand-700 hover:text-brand-800 inline-flex items-center gap-1 font-semibold">
              See audit log <ChevronRight className="size-3" />
            </Link>
          </div>
          <div className="mt-4 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={cardSeries} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="2 4" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: "#64748B", fontSize: 11 }} interval={4} />
                <YAxis tickLine={false} axisLine={false} tick={{ fill: "#64748B", fontSize: 11 }} width={32} />
                <ReferenceLine y={cardAvg} stroke="#94A3B8" strokeDasharray="4 4" />
                <Tooltip content={<ChartTip suffix=" cards" />} cursor={{ stroke: "#0F766E", strokeWidth: 1, strokeDasharray: "4 4" }} />
                <Line type="monotone" dataKey="cards" stroke="#0F766E" strokeWidth={2} dot={false} activeDot={{ r: 4, fill: "#0F766E" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Payment volume bar chart */}
        <div className="bg-surface border border-line rounded-xl p-6">
          <div className="eyebrow">Payment volume</div>
          <div className="mt-1 font-brand text-h1 text-ink tabular-nums">{format.currencyCompact(volTotal)}</div>
          <p className="text-caption text-ink-subtle">last 12 weeks</p>
          <div className="mt-4 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={volSeries} margin={{ top: 8, right: 0, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="2 4" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="week" tickLine={false} axisLine={false} tick={{ fill: "#64748B", fontSize: 11 }} interval={1} />
                <YAxis tickLine={false} axisLine={false} tick={{ fill: "#64748B", fontSize: 11 }} width={36} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                <Tooltip content={<ChartTip currency />} cursor={{ fill: "rgba(15,118,110,0.08)" }} />
                <Bar dataKey="volume" fill="#0F766E" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Activity + Quick actions */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-surface border border-line rounded-xl">
          <div className="px-6 py-4 border-b border-line flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity className="size-4 text-ink-muted" />
              <h2 className="text-h3 text-ink font-semibold">Recent admin activity</h2>
            </div>
            <Link to="/admin/audit" className="text-caption font-semibold text-brand-700 hover:text-brand-800">View all</Link>
          </div>
          <ul className="divide-y divide-line/60">
            {stats.recentActivity.slice(0, 8).map((ev) => (
              <li key={ev.id} className="px-6 py-3.5 flex items-start gap-3">
                <ActivityIcon type={ev.action} />
                <div className="flex-1 min-w-0">
                  <div className="text-body-sm text-ink truncate">{ev.summary}</div>
                  <div className="mt-0.5 text-caption text-ink-subtle">
                    <ActorTag actorId={ev.actorId} /> · {format.relative(ev.at)} · <span className="font-mono text-code">{ev.action}</span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-surface border border-line rounded-xl p-6">
          <div className="eyebrow mb-3">Quick actions</div>
          <div className="space-y-2">
            <QuickAction to="/admin/deals/new" title="Publish a new deal" body="Set rate, cap, deadline, and validation rules." />
            <QuickAction to="/admin/applications" title="Review applications" body={`${stats.pendingApplicationsCount} waiting in queue`} />
            <QuickAction to="/admin/submissions" title="Triage flagged submissions" body={`${stats.flaggedSubmissionsCount} cards need a decision`} />
            <QuickAction to="/admin/payments" title="Run payments" body="Generate ACH file for the next cycle" />
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, subline, link, tone = "brand", attention = false }) {
  const tones = {
    brand: "bg-brand-50 text-brand-700",
    info: "bg-sky-50 text-sky-700",
    warning: "bg-amber-50 text-amber-700",
    flagged: "bg-orange-50 text-orange-700",
  };
  return (
    <Link to={link} className={`group bg-surface border rounded-xl p-5 hover:shadow-sm transition-all ${attention ? "border-line ring-1 ring-orange-200" : "border-line"}`}>
      <div className="flex items-center justify-between">
        <div className={`size-8 rounded-md grid place-items-center ${tones[tone]}`}>{icon}</div>
        <ArrowUpRight className="size-4 text-ink-hint group-hover:text-brand-700 transition-colors" />
      </div>
      <div className="mt-4 font-brand text-[34px] font-bold text-ink leading-none tabular-nums">{value}</div>
      <div className="mt-2 text-body-sm text-ink font-medium">{label}</div>
      <div className="mt-0.5 text-caption text-ink-subtle truncate">{subline}</div>
    </Link>
  );
}

function ChartTip({ active, payload, label, currency = false, suffix = "" }) {
  if (!active || !payload?.length) return null;
  const v = payload[0].value;
  return (
    <div className="bg-surface border border-line rounded-md shadow-md px-3 py-2 text-caption">
      <div className="text-ink-subtle">{label}</div>
      <div className="mt-0.5 text-ink font-semibold tabular-nums">
        {currency ? format.currency(v) : `${format.count(v)}${suffix}`}
      </div>
    </div>
  );
}

function ActorTag({ actorId }) {
  if (actorId?.startsWith("adm_")) {
    const a = getAdminById(actorId);
    if (a) return <span className="text-ink font-medium">{a.firstName} {a.lastName[0]}.</span>;
  }
  if (actorId?.startsWith("mbr_")) return <span className="text-ink-muted">Member</span>;
  return <span className="text-ink-muted">{actorId}</span>;
}

function ActivityIcon({ type = "" }) {
  const cls = "size-4 shrink-0 mt-0.5";
  if (type.includes("publish") || type.includes("approve")) return <CheckCircle2 className={`${cls} text-emerald-600`} />;
  if (type.includes("reject") || type.includes("flag")) return <AlertTriangle className={`${cls} text-orange-600`} />;
  if (type.includes("Payment") || type.includes("export") || type.includes("decrypt")) return <Send className={`${cls} text-brand-700`} />;
  return <Activity className={`${cls} text-ink-hint`} />;
}

function QuickAction({ to, title, body }) {
  return (
    <Link to={to} className="block p-3.5 rounded-md border border-line hover:border-brand-700 hover:bg-brand-50/40 transition-all group">
      <div className="flex items-center justify-between">
        <div className="text-body-sm font-semibold text-ink">{title}</div>
        <ChevronRight className="size-4 text-ink-hint group-hover:text-brand-700 group-hover:translate-x-0.5 transition-all" />
      </div>
      <div className="mt-0.5 text-caption text-ink-subtle">{body}</div>
    </Link>
  );
}
